import React from "react";
import { Database, Layers } from "lucide-react";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import { INSIGHT_COMPLETED_STATUSES, type InsightProvenanceMember } from "../../types/insightApi";

/** Sentinel tab value for "the whole cluster" — the merged topology of every member. */
export const ALL_MEMBERS = "__all__";

export interface ClusterMemberSelectorProps {
	/** Contributing members of the cluster, from the merged response's `provenance`. */
	members: InsightProvenanceMember[];
	/** `null` while the merged (all-members) topology is shown, otherwise the scoped member's ctxrUuid. */
	activeMemberCtxrUuid: string | null;
	onSelectMember: (ctxrUuid: string | null) => void;
}

/** Abbreviated CTXR label — the same `CTXR-xxxxxx` shorthand the status banner and the scope browser use, so the three surfaces name a member identically. */
export function memberShortLabel(member: InsightProvenanceMember): string {
	return `CTXR-${member.ctxrUuid.slice(-6)}`;
}

/**
 * Floating member switcher for a cluster topology, mirroring BGP Visibility's
 * `ClusterSelector`: a compact segmented control over the top-centre of the
 * canvas, one tab per member plus an "All members" tab.
 *
 * Labelled by CTXR name rather than HA role: unlike BGP's `/visibility`
 * payload, Insight's digest `provenance` carries no Primary/Secondary role, and
 * inventing one would be a guess. The tab shows the member's name with its
 * abbreviated CTXR id, plus whether its digest actually completed.
 *
 * Selecting a member re-scopes the topology REQUEST to that member (as a
 * `reflector` source) rather than filtering the merged graph in the browser —
 * the backend then lays that member's topology out on its own, exactly as it
 * would for a standalone reflector, so no client-side surgery on ELK output is
 * needed.
 */
export const ClusterMemberSelector: React.FC<ClusterMemberSelectorProps> = ({
	members,
	activeMemberCtxrUuid,
	onSelectMember,
}) => {
	// One member is not a choice; zero means the response carried no provenance.
	if (members.length < 2) return null;

	const tabs = [
		{
			label: (
				<span className="flex items-center gap-1.5 py-0.5">
					<Layers className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
					<span className="text-[11px] font-semibold leading-none">All members</span>
				</span>
			),
			value: ALL_MEMBERS,
		},
		...members.map((member) => {
			const digested = INSIGHT_COMPLETED_STATUSES.has(member.status);
			return {
				label: (
					<span className="flex items-center gap-1.5 py-0.5">
						<Database className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
						<span className="flex flex-col items-start gap-0.5">
							<span className="flex items-center gap-1.5">
								<span className="text-[11px] font-semibold leading-none">{member.name || memberShortLabel(member)}</span>
								<span
									className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none ${
										digested
											? "bg-semantic-bg-success-secondary text-semantic-text-success"
											: "bg-semantic-bg-secondary-subtle text-semantic-text-tertiary"
									}`}
								>
									{digested ? "Digested" : member.status}
								</span>
							</span>
							<span className="text-[10px] font-normal leading-none text-semantic-text-tertiary">
								{memberShortLabel(member)}
							</span>
						</span>
					</span>
				),
				value: member.ctxrUuid,
			};
		}),
	];

	return (
		<div
			data-testid="cluster-member-selector"
			className="pointer-events-auto absolute left-1/2 top-4 z-[60] w-fit -translate-x-1/2"
		>
			<GenericTabs
				aria-label="Cluster member"
				tabs={tabs}
				activeTab={activeMemberCtxrUuid ?? ALL_MEMBERS}
				onTabChange={(value) => onSelectMember(value === ALL_MEMBERS ? null : value)}
				size="xs"
				fullWidth={false}
			/>
		</div>
	);
};

export default ClusterMemberSelector;
